import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ClassLabel from "./ClassLabel";
import PictureListActions from "./PictureListActions";
import Container from "components/__reusable/Container";
import MessageDialog from "components/__reusable/MessageDialog";
import PictureThumbnail from "components/__reusable/PictureThumbnail";
import ProgressBar from "components/__reusable/ProgressBar";
import PictureDialog from "components/UploadPicture/PictureDialog/PictureDialog";
import PrimaryActionButton from "components/__reusable/PrimaryActionButton";
import SecondaryActionButton from "components/__reusable/SecondaryActionButton";
import ClassDropdown from "components/__reusable/ClassDropdown";
import useAppSelector from "hooks/useAppSelector";
import useAppDispatch from "hooks/useAppDispatch";
import { getUnpublishedPictures } from "features/picture/pictureAction";
import { setPictures } from "features/picture/pictureSlice";
import { setSuccessStatus } from "features/success/successSlice";
import { changePictureClass } from "services/pictureServices";
import type { Picture } from "types/Pictures";
import { getUnpublishablePicture } from "utilities/PictureStatusFilter";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { PiArrowFatLinesUpFill } from "react-icons/pi";

const Pictures = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const fetchUnpublishedPictures = () => {
    const classId = parseInt(id || "", 10);
    if (isNaN(classId)) return;
    dispatch(getUnpublishedPictures({ classId }));
  };
  const callFetchUnpublishedPictures = useCallback(fetchUnpublishedPictures, [
    id,
    dispatch,
  ]);
  useEffect(() => {
    callFetchUnpublishedPictures();
  }, [callFetchUnpublishedPictures]);

  const { pictures } = useAppSelector((state) => state.picture);
  const [viewIndex, setViewIndex] = useState(-1);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isReuploading, setIsReuploading] = useState(false);
  const [showReuploadDialog, setShowReuploadDialog] = useState(false);
  const [showReuploadingDialog, setShowReuploadingDialog] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [reuploadTotal, setReuploadTotal] = useState(0);
  const [reuploadProgress, setReuploadProgress] = useState(0);
  const [selectedPictureIds, setSelectedPictureIds] = useState<number[]>([]);
  const reuploadLock = useRef(false);
  const reuploadAbortController = useRef<AbortController | null>(null);
  const reuploadCancelled = useRef(false);
  const [unpublishablePictures, setUnpublishablePictures] = useState<Picture[]>(
    []
  );
  const filterPicture = () => {
    if (pictures.length === 0) {
      setUnpublishablePictures([]);
    }
    console.log(unpublishablePictures);
    if (unpublishablePictures.length === 0) {
      const updatedUnpublishablePictures = getUnpublishablePicture(pictures);
      setUnpublishablePictures(updatedUnpublishablePictures);
    } else {
      const temp = [];
      for (let unpublishablePicture of unpublishablePictures) {
        console.log(unpublishablePicture);
        const picture = pictures.find((p) => p.id === unpublishablePicture.id);
        if (picture) {
          temp.push(picture);
        }
      }
      setUnpublishablePictures(temp);
    }
  };

  useEffect(() => {
    filterPicture();
  }, [pictures]);

  useEffect(() => {
    if (!isSelectionMode) return;
    const availableIds = new Set(
      unpublishablePictures.map((picture) => picture.id)
    );
    setSelectedPictureIds((prev) => prev.filter((id) => availableIds.has(id)));
  }, [isSelectionMode, unpublishablePictures]);

  const viewPicture = (pictureId: number) => {
    if (!unpublishablePictures.length) return;
    const index = unpublishablePictures.findIndex(
      (picture) => picture.id === pictureId
    );
    setViewIndex(index);
  };
  const viewPreviousPicture = () => {
    let index = viewIndex;
    if (index === 0) return;
    index--;
    setViewIndex(index);
  };
  const viewNextPicture = () => {
    if (!unpublishablePictures.length) return;
    let index = viewIndex;
    if (index === unpublishablePictures.length - 1) return;
    index++;
    setViewIndex(index);
  };
  const handleKeyboardEvent = (e: KeyboardEvent) => {
    if (e.keyCode === 39) {
      //arrow right key
      viewNextPicture();
      return;
    }
    if (e.keyCode === 37) {
      //arrow left key
      viewPreviousPicture();
      return;
    }
  };
  useEffect(() => {
    window.removeEventListener("keyup", handleKeyboardEvent);
    window.addEventListener("keyup", handleKeyboardEvent);
    return () => {
      window.removeEventListener("keyup", handleKeyboardEvent);
    };
  }, [viewIndex]);

  const handleSelectMode = () => {
    setIsSelectionMode(true);
  };

  const handleSelectAll = () => {
    setSelectedPictureIds(unpublishablePictures.map((picture) => picture.id));
  };

  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    setSelectedPictureIds([]);
    setShowReuploadDialog(false);
  };

  const handleOpenReuploadDialog = () => {
    if (!isSelectionMode || selectedPictureIds.length === 0) return;
    const currentClassId = parseInt(id || "", 10);
    setSelectedClassId(!isNaN(currentClassId) ? currentClassId.toString() : "");
    setShowReuploadDialog(true);
  };

  const handleConfirmReupload = async () => {
    if (
      reuploadLock.current ||
      isReuploading ||
      !isSelectionMode ||
      selectedPictureIds.length === 0
    ) {
      return;
    }
    const currentClassId = parseInt(id || "", 10);
    const targetClassId = parseInt(selectedClassId, 10);
    if (isNaN(currentClassId) || isNaN(targetClassId)) return;

    const selectedIds = [...selectedPictureIds];
    const total = selectedIds.length;
    setShowReuploadDialog(false);
    setReuploadTotal(total);
    setReuploadProgress(0);

    try {
      reuploadCancelled.current = false;
      reuploadLock.current = true;
      setIsReuploading(true);
      setShowReuploadingDialog(true);
      reuploadAbortController.current = new AbortController();
      await changePictureClass(
        {
          picture_ids: selectedIds,
          class_id: targetClassId,
        },
        reuploadAbortController.current.signal
      );
      if (reuploadCancelled.current) return;
      setReuploadProgress(total);
      setUnpublishablePictures((prev) =>
        prev.filter((picture) => !selectedIds.includes(picture.id))
      );
      dispatch(
        setPictures({
          pictures: pictures.filter(
            (picture) => !selectedIds.includes(picture.id)
          ),
        })
      );
      setIsSelectionMode(false);
      setSelectedPictureIds([]);
      dispatch(
        setSuccessStatus({
          title: "アップロード完了",
          message: `${total}件のアップロードをやり直しました`,
          showViewScreenAction: true,
        })
      );
    } catch (error) {
      if (
        (error as { code?: string })?.code === "ERR_CANCELED" ||
        (error as { name?: string })?.name === "CanceledError"
      ) {
        return;
      }
      console.error(error);
      alert(t("_generic._error"));
    } finally {
      setIsReuploading(false);
      setShowReuploadingDialog(false);
      reuploadLock.current = false;
      reuploadAbortController.current = null;
      reuploadCancelled.current = false;
    }
  };

  const handleCloseReuploadingDialog = () => {
    setShowReuploadingDialog(false);
  };

  const handleCancelReuploading = () => {
    if (!isReuploading) return;
    reuploadCancelled.current = true;
    reuploadAbortController.current?.abort();
    setShowReuploadingDialog(false);
    setReuploadProgress(0);
    setReuploadTotal(0);
  };

  const handleToggleSelection = (pictureId: number) => {
    setSelectedPictureIds((prev) =>
      prev.includes(pictureId)
        ? prev.filter((id) => id !== pictureId)
        : [...prev, pictureId]
    );
  };

  return (
    <div className="h-full flex flex-col space-y-3 lg:space-y-5">
      <div className="flex justify-between items-center">
        <ClassLabel />
        <PictureListActions
          isSelectionMode={isSelectionMode}
          selectedCount={selectedPictureIds.length}
          isReuploading={isReuploading}
          onSelectMode={handleSelectMode}
          onSelectAll={handleSelectAll}
          onCancelSelection={handleCancelSelection}
          onReupload={handleOpenReuploadDialog}
        />
      </div>
      <div className="flex-1">
        <Container>
          <div className="flex-1 h-full overflow-y-auto relative">
            <div className="w-full h-full absolute">
              <div className="flex flex-wrap justify-start px-2 pt-1">
                {unpublishablePictures.length === 0 ? (
                  <p>{t("_generic._no_data")}</p>
                ) : (
                  unpublishablePictures.map((picture) => (
                    <button
                      type="button"
                      key={picture.id}
                      className="w-16 h-16 me-[1px] mb-[1px] rounded-md box-border relative"
                      onClick={() => {
                        if (isSelectionMode) {
                          handleToggleSelection(picture.id);
                          return;
                        }
                        if (picture.thumbnail_picture) {
                          viewPicture(picture.id);
                        }
                      }}
                    >
                      <PictureThumbnail
                        imageSrc={picture.thumbnail_picture}
                        name="picture"
                        rounded={false}
                      />
                      {isSelectionMode &&
                        selectedPictureIds.includes(picture.id) && (
                          <span className="absolute inset-0 z-10 box-border border-2 border-pink-500 pointer-events-none" />
                        )}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </Container>
      </div>
      {viewIndex > -1 && unpublishablePictures[viewIndex] !== undefined && (
        <PictureDialog
          picture={unpublishablePictures[viewIndex]}
          onPrevious={viewIndex > 0 ? () => viewPreviousPicture() : null}
          onNext={
            viewIndex < unpublishablePictures.length - 1
              ? () => viewNextPicture()
              : null
          }
          onClose={() => {
            setViewIndex(-1);
            const updatedUnpublishablePictures =
              getUnpublishablePicture(pictures);
            setUnpublishablePictures(updatedUnpublishablePictures);
          }}
        />
      )}
      {showReuploadDialog && (
        <MessageDialog>
          <div className="space-y-5">
            <h2 className="text-xl text-center font-semibold">
              {selectedPictureIds.length}件のアップロードをやり直します
            </h2>
            <div className="space-y-2">
              <label className="text-sm text-[#3F3F3F]">
                {t("_unpublished_picture._class", { defaultValue: "クラス名" })}
              </label>
              <ClassDropdown
                currentClassId={selectedClassId}
                onChange={(value) => setSelectedClassId(value.toString())}
              />
            </div>
            <div className="flex justify-between items-center space-x-4">
              <div className="w-1/2">
                <SecondaryActionButton
                  label={t("_generic._cancel")}
                  icon={<AiOutlineCloseCircle />}
                  onClick={() => setShowReuploadDialog(false)}
                  disabled={isReuploading}
                />
              </div>
              <div className="w-1/2">
                <PrimaryActionButton
                  label={t("_upload._upload")}
                  icon={<PiArrowFatLinesUpFill />}
                  onClick={handleConfirmReupload}
                  disabled={!selectedClassId || isReuploading}
                  loading={isReuploading}
                />
              </div>
            </div>
          </div>
        </MessageDialog>
      )}
      {isReuploading && showReuploadingDialog && (
        <MessageDialog>
          <div className="space-y-3">
            <h2 className="text-2xl text-center font-semibold">
              {t("_upload._uploading")}
            </h2>
            <p className="text-center">{t("_upload._please_wait")}</p>
            <p className="text-sm text-center">
              {t("_upload._upload_progress", {
                totalUploaded: reuploadProgress,
                totalPictures: reuploadTotal,
              })}
            </p>
            <ProgressBar
              progress={
                reuploadTotal === 0
                  ? 0
                  : (reuploadProgress / reuploadTotal) * 100
              }
            />
            <div className="w-full flex flex-col sm:flex-row justify-center items-center gap-3">
              <button
                className="w-full h-full py-2 bg-white border border-red-500 text-red-500 font-semibold rounded flex items-center justify-center space-x-2"
                onClick={handleCancelReuploading}
              >
                <AiOutlineCloseCircle width={20} height={20} color="#ef4444" />
                <span>{t("_upload._cancel")}</span>
              </button>
              <PrimaryActionButton
                label={t("_upload._close")}
                onClick={handleCloseReuploadingDialog}
              />
            </div>
          </div>
        </MessageDialog>
      )}
    </div>
  );
};

export default Pictures;
